import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  authenticationContext,
  commonActions,
  commonActionUtils,
  HttpMethods,
  uiActions
} from '@codametrix/ui-common';
import { requestJSON } from '@codametrix/client-api';
import { loggerFactory } from '@codametrix/client-logging';
import { runSessionRenew, sendActivityHeartbeat } from '@codametrix/client-session';
import history from '../history';
import {
  fetchContexts as contextApi,
  postContextChoiceNavigation,
  shouldRefresh,
  tokenAvailable
} from './contexts.action';
import { jumpURL } from './jump';
import { navigateContext } from './navigation.actions';
import { dismissModal } from './feedback.actions';
import { loading, logout, setActiveUser } from './ui-core.actions';
import { dispatchSessionHeartbeatMiss } from '../core/session-heartbeat-miss';

const { UserInterface } = commonActions;
const { shimContext } = commonActionUtils;
const logger = loggerFactory('BootstrapActions');

const fetchContexts = createAsyncThunk<
  CMx.Context,
  CMx.NavData | null,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.BOOTSTRAP,
  async (navData, { dispatch, getState }): Promise<CMx.Context> => {
    const state = getState() as any;
    // Prefer context from state if available, as navData may have been captured
    // before the authentication channel resolved and updated the state.
    const currentContext = state.ui?.context;
    const optContext = currentContext && currentContext.activeContext !== null 
      ? currentContext 
      : navData?.context;

    // in some cases the activeContext will already have been
    // determined.  if this is the case, then simply return
    // the context.  if the user needs to select the context
    // later we should provision this in the UI.
    if (optContext && optContext.activeContext !== null) {
      authenticationContext.setContextId(optContext.activeContextId);
      dispatch(
        uiActions.saveServiceLineCapability(
          optContext?.activeContext?.serviceLineName || ''
        )
      );
      // Several views (Organization admin, Users admin, QA navigation) gate
      // their own data fetch on state.login.auth being truthy — a legacy
      // holdover from bearer-token auth that's otherwise never set when
      // bootstrap skips the Login flow entirely (BFF session, direct URL).
      dispatch(
        tokenAvailable({
          token: optContext.jwtToken || authenticationContext.getToken() || '',
          expiryTimeToLive: optContext.expiryTimeToLive
        }) as any
      );
      dispatch(uiActions.awaitingResources(false));
      // The bare /cmx/ root (e.g. the pre-auth -> dashboard handoff, or a hard
      // reload restoring context from sessionStorage) has no destination of
      // its own -- redirect to the same default-homepage/first-group resource
      // the cold-boot path below resolves. Any other path is already the
      // intended destination.
      const isBareRootEarlyReturn =
        history.location.pathname.replace(/\/$/, '') === '/cmx';
      if (isBareRootEarlyReturn) {
        await dispatch(
          navigateContext({
            context: optContext,
            homepagePreference: state.ui?.homepagePreference
          }) as any
        );
      }
      return optContext;
    } else {
      const userContexts = await contextApi(dispatch as any);

      const shimmed = shimContext(userContexts);
      const { user } = userContexts;

      dispatch(setActiveUser(user));

      // Under a BFF session, GET /contexts/v1 already reflects the
      // session's active org — apply it to ui state unconditionally so
      // roles/actionGroups/shortcuts are populated regardless of which
      // internal path jumpURL takes (its "cold boot jump" branch below
      // can leave chooseContext undispatched, e.g. when app-jump's
      // saveJump() has written jump data with no contextId, which is the
      // normal case under a BFF session since no client-held token exists
      // for saveJump to capture a contextId from).
      dispatch(uiActions.chooseContext(shimmed));
      // See the early-return branch above for why this is needed even
      // though GET /contexts/v1 doesn't mint a real jwtToken. Keep a token
      // already restored on reload: dispatching '' clears it, and with bearer
      // auth on, the next api() call throws UnauthenticatedError -> LOGOUT.
      dispatch(
        tokenAvailable({
          token: shimmed.jwtToken || authenticationContext.getToken() || '',
          expiryTimeToLive: shimmed.expiryTimeToLive
        }) as any
      );
      // Same service-line/actionGroups setup as choosing an org from the
      // picker (postContextChoiceNavigation). A deep-link/direct-URL
      // bootstrap is already on the page it wants, so skip its navigation
      // step — except on the bare /cmx/ root (e.g. the hard reload
      // triggered by an org-context-switch broadcast, store.ts
      // onOrgContextSwitched), which has no destination of its own and
      // needs the same default-homepage/first-group resolution the org
      // picker gets.
      const isBareRoot = history.location.pathname.replace(/\/$/, '') === '/cmx';
      await postContextChoiceNavigation(shimmed, dispatch, true, undefined, isBareRoot);

      // Navigation is already implied by the current URL — no need to
      // resolve a default homepage here (that's login-flow territory).
      // On the bare root, postContextChoiceNavigation above already redirected
      // to the right resource — jumpURL replays a saved-jump URL from
      // app-jump.ts's sessionStorage (written on a *prior* page's unload,
      // within its 30-minute TTL), which would otherwise unconditionally
      // history.replace() over that fresh redirect with a stale destination.
      // replay: false — the current URL is the destination; a saved jump written by
      // the previously unloaded page (e.g. cases) must not override a pasted URL (worklist).
      const jumped = isBareRoot
        ? false
        : await jumpURL(shimmed, dispatch as any, { replay: false });
      if (!jumped) {
        dispatch(loading(false));
      }
      dispatch(
        uiActions.saveServiceLineCapability(
          shimmed?.activeContext?.serviceLineName || ''
        ) as any
      );
      dispatch(uiActions.awaitingResources(false));
      return shimmed;
    }
  }
);

type submitArgs = {
  name: string;
  params?: { [key: string]: any };
};

const modalSubmit = createAsyncThunk<
  void,
  submitArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.MODAL_SUBMIT,
  async (args, { dispatch }) => {
    const { name, params } = args;

    switch (name) {
      case UserInterface.LOGOUT:
        dispatch(logout());
        if (params?.isSSOUser && params?.orgCode?.length) {
          history.replace(`/sso/${params?.orgCode}`);
        } else {
          history.push(`/`);
        }
        break;
      case UserInterface.REFRESH: {
        dispatch(shouldRefresh());
        dispatch(dismissModal());
        const contextId = authenticationContext.getContextId();
        await runSessionRenew({
          role: 'initiator',
          legacyRenew: async () => {
            if (contextId == null) {
              return;
            }
            try {
              const freshContext = await requestJSON<CMxAPI.ContextAPI>(
                `/contexts/v1`,
                {
                  method: HttpMethods.PUT,
                  body: JSON.stringify({ organizationId: contextId })
                }
              );
              dispatch(
                tokenAvailable({
                  token: freshContext.jwtToken,
                  expiryTimeToLive: freshContext.expiryTimeToLive
                }) as any
              );
            } catch (e) {
              logger.error(`activity-prompt context renewal failed`, e);
            }
          },
          onToken: ({ token, expiryTimeToLive }) => {
            dispatch(tokenAvailable({ token, expiryTimeToLive }) as any);
          },
          onUnauthorized: () => dispatchSessionHeartbeatMiss(dispatch),
          onRejectNoPresence: () => {
            logger.warn('session renew rejected: no presence since last renewal');
          }
        });
        await sendActivityHeartbeat({
          contextId,
          shouldSendHeartbeat: true,
          onSessionMiss: () => dispatchSessionHeartbeatMiss(dispatch)
        });
        break;
      }
      default:
        break;
    }
  }
);

export { fetchContexts, modalSubmit };
